import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, ChevronLeft, ChevronRight } from 'lucide-angular';

export interface CalendarDay {
  date: number;
  month: number;
  year: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isPast: boolean;
}

@Component({
  selector: 'app-calendar-picker',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './calendar-picker.html',
  styleUrl: './calendar-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarPickerComponent implements OnInit, OnChanges {
  /** Currently selected date in 'YYYY-MM-DD' format */
  @Input() selectedDate = '';

  /** Theme color for highlights */
  @Input() themeColor = '#22C55E';

  /** Emits selected date in 'YYYY-MM-DD' format */
  @Output() readonly dateSelected = new EventEmitter<string>();

  /** Emits when the user clicks outside / wants to close the calendar */
  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = { ChevronLeft, ChevronRight };

  protected calendarMonth = 0;
  protected calendarYear = 2026;
  protected calendarDays: CalendarDay[] = [];

  protected readonly weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  protected readonly monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // ─── Lifecycle ───────────────────────────────────────────

  ngOnInit(): void {
    this.initCalendarFromDate();
    this.buildCalendarDays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDate'] && !changes['selectedDate'].firstChange) {
      this.initCalendarFromDate();
      this.buildCalendarDays();
    }
  }

  // ─── Navigation ──────────────────────────────────────────

  protected prevMonth(): void {
    this.calendarMonth--;
    if (this.calendarMonth < 0) {
      this.calendarMonth = 11;
      this.calendarYear--;
    }
    this.buildCalendarDays();
  }

  protected nextMonth(): void {
    this.calendarMonth++;
    if (this.calendarMonth > 11) {
      this.calendarMonth = 0;
      this.calendarYear++;
    }
    this.buildCalendarDays();
  }

  // ─── Selection ───────────────────────────────────────────

  protected selectDate(day: CalendarDay): void {
    if (day.isPast && !day.isToday) {
      return;
    }
    const formatted = this.formatDateForInput(
      new Date(day.year, day.month, day.date),
    );
    this.dateSelected.emit(formatted);
  }

  protected selectToday(): void {
    const today = new Date();
    this.calendarMonth = today.getMonth();
    this.calendarYear = today.getFullYear();
    this.dateSelected.emit(this.formatDateForInput(today));
  }

  // ─── Helpers ─────────────────────────────────────────────

  private initCalendarFromDate(): void {
    if (this.selectedDate) {
      const d = new Date(this.selectedDate + 'T00:00:00');
      this.calendarMonth = d.getMonth();
      this.calendarYear = d.getFullYear();
    } else {
      const today = new Date();
      this.calendarMonth = today.getMonth();
      this.calendarYear = today.getFullYear();
    }
  }

  private buildCalendarDays(): void {
    const days: CalendarDay[] = [];
    const firstDow = new Date(
      this.calendarYear,
      this.calendarMonth,
      1,
    ).getDay();
    const daysInMonth = new Date(
      this.calendarYear,
      this.calendarMonth + 1,
      0,
    ).getDate();
    const daysInPrev = new Date(
      this.calendarYear,
      this.calendarMonth,
      0,
    ).getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sel = this.selectedDate
      ? new Date(this.selectedDate + 'T00:00:00')
      : null;
    if (sel) {
      sel.setHours(0, 0, 0, 0);
    }

    // Previous month padding
    for (let i = firstDow - 1; i >= 0; i--) {
      const day = daysInPrev - i;
      let m = this.calendarMonth - 1;
      let y = this.calendarYear;
      if (m < 0) {
        m = 11;
        y--;
      }
      const d = new Date(y, m, day);
      d.setHours(0, 0, 0, 0);
      days.push({
        date: day,
        month: m,
        year: y,
        isCurrentMonth: false,
        isToday: d.getTime() === today.getTime(),
        isSelected: sel ? d.getTime() === sel.getTime() : false,
        isPast: d < today,
      });
    }

    // Current month
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(this.calendarYear, this.calendarMonth, day);
      d.setHours(0, 0, 0, 0);
      days.push({
        date: day,
        month: this.calendarMonth,
        year: this.calendarYear,
        isCurrentMonth: true,
        isToday: d.getTime() === today.getTime(),
        isSelected: sel ? d.getTime() === sel.getTime() : false,
        isPast: d < today,
      });
    }

    // Next month padding (fill to complete rows)
    const totalNeeded = days.length > 35 ? 42 : 35;
    const remaining = totalNeeded - days.length;
    for (let day = 1; day <= remaining; day++) {
      let m = this.calendarMonth + 1;
      let y = this.calendarYear;
      if (m > 11) {
        m = 0;
        y++;
      }
      const d = new Date(y, m, day);
      d.setHours(0, 0, 0, 0);
      days.push({
        date: day,
        month: m,
        year: y,
        isCurrentMonth: false,
        isToday: d.getTime() === today.getTime(),
        isSelected: sel ? d.getTime() === sel.getTime() : false,
        isPast: d < today,
      });
    }

    this.calendarDays = days;
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
